import { useState, useCallback } from 'react';
import type { User } from '@auth0/auth0-react';
import toast from 'react-hot-toast';
import { getAccessTokenSilently } from '@/components/auth/Auth0Provider';
import { API_BASE_URL } from '@/constants/api';
import { getBreadcrumbs, getSessionContext } from '../utils/feedbackTracker';
import { FeedbackCategory, FeedbackPayload, ElementMetadata } from '../types';
import { getBrowserContext, getAppContext } from '../utils/feedbackContext';
import { MIN_DESCRIPTION_LENGTH } from '../constants';
import { cleanBackendMessage, formatHttpError, formatNetworkError } from '@/api/formatError';

interface UseFeedbackSubmissionOptions {
  pathname: string;
  user?: User;
}

interface SubmitFeedbackParams {
  category: FeedbackCategory;
  description: string;
  selectedElement: ElementMetadata | null;
  consoleErrors: string[];
}

interface UseFeedbackSubmissionReturn {
  isSubmitting: boolean;
  submitFeedback: (params: SubmitFeedbackParams) => Promise<boolean>;
}

export function useFeedbackSubmission({
  pathname,
  user,
}: UseFeedbackSubmissionOptions): UseFeedbackSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitFeedback = useCallback(
    async ({
      category,
      description,
      selectedElement,
      consoleErrors,
    }: SubmitFeedbackParams): Promise<boolean> => {
      const trimmedDescription = description.trim();
      if (trimmedDescription.length < MIN_DESCRIPTION_LENGTH) {
        toast.error(`Please provide at least ${MIN_DESCRIPTION_LENGTH} characters of feedback.`);
        return false;
      }

      setIsSubmitting(true);

      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (authError) {
        console.error('Failed to acquire auth token:', authError);
        toast.error('Authentication required. Please refresh the page and try again.');
        setIsSubmitting(false);
        return false;
      }

      try {
        const authTime =
          user?.['https://butlr.io/auth_time'] ?? (user as Record<string, unknown>)?.auth_time;

        const payload: FeedbackPayload = {
          category,
          description: trimmedDescription,
          page_url: window.location.href,
          browser_context: getBrowserContext(),
          app_context: getAppContext(),
          session_context: getSessionContext(pathname || '', authTime as number | undefined),
          breadcrumbs: getBreadcrumbs(),
          console_errors: consoleErrors.length > 0 ? consoleErrors : undefined,
        };

        if (selectedElement) {
          payload.element_metadata = selectedElement;
        }

        const feedbackUrl =
          import.meta.env.VITE_FEEDBACK_API_URL || `${API_BASE_URL}/api/v3/feedback`;
        const response = await fetch(feedbackUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const responseText = await response.text();
          let serverMessage: string | undefined;
          try {
            const errorData = JSON.parse(responseText);
            serverMessage = errorData.error;
          } catch {
            // Non-JSON response — use raw text if short enough to be useful
            if (responseText && responseText.length < 200) {
              serverMessage = responseText;
            } else if (responseText) {
              console.warn(
                'Non-JSON error response from feedback API:',
                response.status,
                responseText.slice(0, 500),
              );
            }
          }
          throw new Error(formatHttpError(response.status, serverMessage));
        }

        toast.success('Thank you for your feedback!');
        return true;
      } catch (error) {
        console.error('Failed to submit feedback:', error);

        const userMessage =
          error instanceof TypeError && error.message.includes('fetch')
            ? formatNetworkError(error)
            : error instanceof Error
            ? cleanBackendMessage(error.message)
            : 'Submission failed. Please try again later.';

        toast.error(userMessage);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [pathname, user],
  );

  return {
    isSubmitting,
    submitFeedback,
  };
}
